// Helpers shared by the upload flows that accept BOTH a file picker and a
// drag&drop (expenses batch upload, interný doklad upload).
//
// The picker enforces the input's `accept` attribute for us; a drop does not —
// `event.dataTransfer.files` hands over whatever the user dragged. These
// helpers close that gap so both paths give the same guarantee.

// Does `file` satisfy one token of an `accept` attribute?
//
// Handles the three token shapes the attribute allows: an extension (".pdf",
// and multi-dot ones like ".isdoc.pdf"), a wildcard family ("image/*") and a
// full MIME type ("application/xml").
function matchesToken(token, name, type) {
  if (token.startsWith(".")) return name.endsWith(token)
  if (token.endsWith("/*")) return type.startsWith(token.slice(0, -1))
  if (type === token) return true

  // Browsers report a dragged .xml as "text/xml" while the input asks for
  // "application/xml". Rejecting that would be stricter than the picker the
  // user could have used instead — same file, same intent. Deliberately a
  // named alias rather than a general "subtypes match" rule, which would let
  // e.g. "x-anything/pdf" through the day someone adds "application/pdf".
  return XML_TYPES.has(token) && XML_TYPES.has(type)
}

const XML_TYPES = new Set(["application/xml", "text/xml"])

// True when `file` is something the given `accept` attribute allows.
// An empty/absent `accept` means "anything", matching the HTML default.
export function matchesAccept(file, accept) {
  const tokens = (accept || "")
    .split(",")
    .map((token) => token.trim().toLowerCase())
    .filter(Boolean)

  if (tokens.length === 0) return true

  const name = (file.name || "").toLowerCase()
  const type = (file.type || "").toLowerCase()

  return tokens.some((token) => matchesToken(token, name, type))
}

// Is this drag carrying files? Dragging selected text or a link across an
// upload panel must keep its ordinary behaviour.
export function carriesFiles(event) {
  return Array.from(event.dataTransfer?.types || []).includes("Files")
}

// While an upload offcanvas is open, its static backdrop covers most of the
// screen. A drop that lands there is still aimed at the panel as far as the
// user is concerned, but it reaches the browser default — which navigates the
// tab to the dropped file and destroys the half-filled panel, the very bug the
// in-panel handlers exist to prevent. Claim those drops for the panel too.
//
// Deliberately narrow: ONLY drops on an offcanvas backdrop, not "every drop on
// the page that isn't inside me". The wider rule happens to be safe today, but
// it would quietly swallow drops meant for another dropzone on the same page —
// e.g. the ISDOC import modal, which shares a page with the internal-income
// offcanvas and has a file input of its own.
//
// `enabled()` must report whether the panel can actually take files right now
// (the locked/upgrade variants render no file input). Returns a teardown
// function for disconnect().
export function captureBackdropDrops({ element, enabled, onFiles, setDragging }) {
  const handler = (event) => {
    // window is the bubble's last stop, so anything inside the panel has
    // already been handled by its own data-action — running again double-adds.
    if (element.contains(event.target)) return
    if (!event.target?.closest?.(".offcanvas-backdrop")) return
    if (!element.classList.contains("show") || !enabled()) return

    if (event.type === "dragleave") {
      // Only when the pointer left the window entirely.
      if (!event.relatedTarget) setDragging(false)
      return
    }

    if (!carriesFiles(event)) return
    event.preventDefault()

    if (event.type === "dragover") {
      if (event.dataTransfer) event.dataTransfer.dropEffect = "copy"
      setDragging(true)
      return
    }

    setDragging(false)
    onFiles(Array.from(event.dataTransfer.files))
  }

  for (const type of ["dragover", "dragleave", "drop"]) {
    window.addEventListener(type, handler)
  }

  return () => {
    for (const type of ["dragover", "dragleave", "drop"]) {
      window.removeEventListener(type, handler)
    }
  }
}

// Split a list of files into the ones `accept` allows and the ones it doesn't.
export function partitionByAccept(files, accept) {
  const accepted = []
  const rejected = []

  for (const file of files) {
    if (matchesAccept(file, accept)) {
      accepted.push(file)
    } else {
      rejected.push(file)
    }
  }

  return { accepted, rejected }
}

// Surface a dismissable warning about a file we refused to take.
//
// Mirrors the server-rendered toast markup from
// FlashMessages::FlashRenderer#create_toast_html — appending it to
// #toast_container is enough, the `toast` Stimulus controller shows and
// auto-hides it from there. (The older hand-rolled version of this helper
// wrote a Bootstrap alert into `.flash-messages`, an element this app has
// never rendered, so every notice it produced was invisible.)
export function flashFileWarning(message) {
  const container = document.getElementById("toast_container")
  if (!container) {
    console.warn(message)
    return
  }

  const toast = document.createElement("div")
  toast.className = "toast"
  toast.setAttribute("role", "alert")
  toast.setAttribute("aria-live", "assertive")
  toast.setAttribute("aria-atomic", "true")
  toast.dataset.controller = "toast"

  const header = document.createElement("div")
  header.className = "toast-header text-white"

  const icon = document.createElement("span")
  icon.className = "material-symbols-outlined text-warning"
  icon.textContent = "warning"
  // Decorative — otherwise a screen reader announces the literal word twice.
  icon.setAttribute("aria-hidden", "true")

  const text = document.createElement("strong")
  text.className = "me-auto"
  text.textContent = message

  const dismiss = document.createElement("button")
  dismiss.type = "button"
  dismiss.className = "btn-close btn-close-white"
  dismiss.dataset.bsDismiss = "toast"
  dismiss.setAttribute("aria-label", "Close")

  header.append(icon, text, dismiss)
  toast.appendChild(header)
  container.appendChild(toast)
}
